import React, { useState } from 'react';
import Icon from 'components/AppIcon';

const ASSET_TYPES = [
{ id: 'character', label: 'Game Character', icon: 'User', color: 'var(--color-primary)', desc: 'Hero, villain, NPC' },
{ id: 'background', label: 'Background', icon: 'Image', color: 'var(--color-secondary)', desc: 'Environment, scene' },
{ id: 'sfx', label: 'Sound Effect', icon: 'Volume2', color: 'var(--color-accent)', desc: 'Jump, hit, collect' },
{ id: 'music', label: 'Background Music', icon: 'Music', color: '#F43F5E', desc: 'Loop, ambient, battle' },
{ id: 'ui', label: 'UI Element', icon: 'Layout', color: '#10B981', desc: 'Button, icon, HUD' },
{ id: 'object', label: 'Game Object', icon: 'Box', color: '#F59E0B', desc: 'Coin, power-up, obstacle' }];


const GENERATED_ASSETS = [
{ id: 1, type: 'character', name: 'Space Hero', thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_10b512154-1773036688081.png", alt: 'Pixel art space hero character with blue armor and jetpack' },
{ id: 2, type: 'background', name: 'Neon City', thumbnail: "https://images.unsplash.com/photo-1653142267767-a66c7cf17bc1", alt: 'Neon-lit cyberpunk city background with glowing signs' },
{ id: 3, type: 'object', name: 'Gold Coin', thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_117b3e5d6-1773036688069.png", alt: 'Shiny gold coin game object with sparkle effect' }];


export default function AIAssetGenerator({ isOpen, onToggle }) {
  const [selectedType, setSelectedType] = useState('character');
  const [assetPrompt, setAssetPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedAssets, setGeneratedAssets] = useState(GENERATED_ASSETS);

  const selectedAssetType = ASSET_TYPES?.find((a) => a?.id === selectedType);

  const handleGenerate = async () => {
    if (!assetPrompt?.trim() || isGenerating) return;
    setIsGenerating(true);
    await new Promise((r) => setTimeout(r, 2000));
    const thumbnails = [
    'https://img.rocket.new/generatedImages/rocket_gen_img_174fa0eb5-1773033727798.png',
    'https://img.rocket.new/generatedImages/rocket_gen_img_1531e24cd-1773033728498.png',
    'https://img.rocket.new/generatedImages/rocket_gen_img_18af588e3-1773033728367.png'];

    const newAsset = {
      id: Date.now(),
      type: selectedType,
      name: assetPrompt?.slice(0, 20) || 'New Asset',
      thumbnail: thumbnails?.[Math.floor(Math.random() * thumbnails?.length)],
      alt: `AI generated ${selectedType} asset: ${assetPrompt}`
    };
    setGeneratedAssets((prev) => [newAsset, ...prev]);
    setAssetPrompt('');
    setIsGenerating(false);
  };

  if (!isOpen) {
    return (
      <button
        onClick={onToggle}
        className="flex items-center gap-2 px-3 py-2 rounded-xl border text-xs font-semibold transition-all hover:opacity-80"
        style={{ borderColor: 'rgba(245,158,11,0.4)', background: 'rgba(245,158,11,0.08)', color: '#F59E0B', fontFamily: 'var(--font-heading)' }}>
        
        <Icon name="Package" size={13} color="#F59E0B" />
        Asset Generator
        <Icon name="ChevronDown" size={12} color="#F59E0B" />
      </button>);

  }

  return (
    <div className="rounded-2xl overflow-hidden animate-fade-in-up" style={{ background: 'var(--color-card)', border: '1px solid rgba(245,158,11,0.3)', boxShadow: 'var(--shadow-md)' }}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: 'var(--color-border)', background: 'rgba(245,158,11,0.06)' }}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: '#F59E0B' }}>
            <Icon name="Package" size={14} color="white" />
          </div>
          <p className="text-xs font-bold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>AI Asset Generator</p>
        </div>
        <button onClick={onToggle} className="w-6 h-6 rounded-lg flex items-center justify-center hover:bg-[var(--color-muted)] transition-all">
          <Icon name="ChevronUp" size={13} color="var(--color-muted-foreground)" />
        </button>
      </div>

      <div className="p-3 flex flex-col gap-3">
        {/* Asset type selector */}
        <div className="grid grid-cols-3 gap-1.5">
          {ASSET_TYPES?.map((type) =>
          <button
            key={type?.id}
            onClick={() => setSelectedType(type?.id)}
            className="flex flex-col items-center gap-1 p-2 rounded-xl border text-xs transition-all"
            style={{
              borderColor: selectedType === type?.id ? type?.color : 'var(--color-border)',
              background: selectedType === type?.id ? `${type?.color}12` : 'var(--color-muted)',
              color: selectedType === type?.id ? type?.color : 'var(--color-muted-foreground)'
            }}>
            
              <Icon name={type?.icon} size={14} color={selectedType === type?.id ? type?.color : 'var(--color-muted-foreground)'} />
              <span style={{ fontFamily: 'var(--font-caption)' }}>{type?.label}</span>
            </button>
          )}
        </div>

        {/* Prompt input */}
        <div className="flex gap-2">
          <input
            value={assetPrompt}
            onChange={(e) => setAssetPrompt(e?.target?.value)}
            onKeyDown={(e) => e?.key === 'Enter' && handleGenerate()}
            placeholder={`Describe your ${selectedAssetType?.label?.toLowerCase()}...`}
            disabled={isGenerating}
            className="flex-1 px-3 py-2 rounded-xl border text-xs focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)] transition-all"
            style={{ borderColor: 'var(--color-border)', background: 'var(--color-muted)', color: 'var(--color-foreground)', fontFamily: 'var(--font-body)' }} />
          
          <button
            onClick={handleGenerate}
            disabled={!assetPrompt?.trim() || isGenerating}
            className="px-3 py-2 rounded-xl text-xs font-semibold flex items-center gap-1.5 transition-all hover:opacity-80 disabled:opacity-40"
            style={{ background: '#F59E0B', color: 'white', fontFamily: 'var(--font-heading)' }}>
            
            {isGenerating ? <Icon name="Loader" size={13} color="white" /> : <Icon name="Wand2" size={13} color="white" />}
            {isGenerating ? '...' : 'Gen'}
          </button>
        </div>

        {/* Generated assets grid */}
        {generatedAssets?.length > 0 &&
        <div>
            <p className="text-xs font-semibold mb-1.5" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>Generated Assets</p>
            <div className="grid grid-cols-3 gap-1.5">
              {generatedAssets?.slice(0, 6)?.map((asset) =>
            <div key={asset?.id} className="relative group rounded-xl overflow-hidden border" style={{ borderColor: 'var(--color-border)', aspectRatio: '1' }}>
                  <img src={asset?.thumbnail} alt={asset?.alt} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <button className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: 'white' }}>
                      <Icon name="Download" size={11} color="#1a1a1a" />
                    </button>
                  </div>
                  <span className="absolute bottom-1 left-1 right-1 text-center text-white" style={{ fontSize: 9, fontFamily: 'var(--font-caption)', textShadow: '0 1px 3px rgba(0,0,0,0.8)' }}>
                    {asset?.name}
                  </span>
                </div>
            )}
            </div>
          </div>
        }
      </div>
    </div>);

}